import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Autocomplete, TextField } from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listStockMovements, type StockMovement } from '../../api/stocuri'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses } from '../../api/depozite'

export default function StockMovementsPage() {
  const [articleId, setArticleId] = useState<string | null>(null)
  const [warehouseId, setWarehouseId] = useState<string | null>(null)
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles() })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses })
  const { data, isLoading } = useQuery({
    queryKey: ['stocuri', 'miscari', articleId, warehouseId],
    queryFn: () => listStockMovements({ articleId: articleId ?? undefined, warehouseId: warehouseId ?? undefined }),
  })

  const columns = useMemo<GridColDef<StockMovement>[]>(() => [
    { field: 'createdAt', headerName: 'Data', width: 160, valueFormatter: (v: string) => new Date(v).toLocaleString('ro-RO') },
    { field: 'articleCode', headerName: 'Cod', width: 120 },
    { field: 'articleDescription', headerName: 'Articol', flex: 1, minWidth: 160 },
    { field: 'warehouseName', headerName: 'Depozit', width: 140 },
    { field: 'type', headerName: 'Tip', width: 150 },
    { field: 'status', headerName: 'Status', width: 100 },
    { field: 'lotNumber', headerName: 'Lot', width: 110, valueGetter: (v: string | null) => v ?? '' },
    { field: 'quantity', headerName: 'Cantitate', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'reference', headerName: 'Referință', flex: 1, minWidth: 160, valueGetter: (v: string | null) => v ?? '' },
    { field: 'createdByUserName', headerName: 'Utilizator', width: 150 },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Stocuri</Typography>
        <Typography variant="inherit" color="text.primary">Mișcări de stoc</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Mișcări de stoc</Typography>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          sx={{ width: 320 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={articleId} onChange={(_, v) => setArticleId(v)}
          renderInput={(params) => <TextField {...params} label="Filtru articol" />}
        />
        <Autocomplete
          sx={{ width: 240 }} options={warehouses?.map((w) => w.id) ?? []}
          getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
          value={warehouseId} onChange={(_, v) => setWarehouseId(v)}
          renderInput={(params) => <TextField {...params} label="Filtru depozit" />}
        />
      </Stack>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}
